import React, { useRef } from 'react';

import type { Column, ReorderColumn } from '../../../../common/types';
import { HeaderCell } from '../../../../common/ui/header-cell/styled';
import InteractiveHeaderCellContent from './interactive';
import ReadOnlyHeaderCellContent from './read-only';

type Props = {
	column: Column;
	index: number;
	instanceId: symbol;
	isDraggable: boolean;
	reorderColumn: ReorderColumn;
	totalColumns: number;
	totalColumnsExcludingMenu: number;
	visibleTableHeight?: number;
};

export const TableHeaderCell = ({
	column,
	index,
	instanceId,
	isDraggable,
	reorderColumn,
	totalColumns,
	totalColumnsExcludingMenu,
	visibleTableHeight = 0,
}: Props) => {
	const headerRef = useRef<HTMLTableCellElement | null>(null);
	const {
		isSortable: doesColumnAllowSorting = true,
		onSortOrderChanged,
		shouldResizeWithTable,
		width,
	} = column;
	const isSortable = onSortOrderChanged !== undefined && doesColumnAllowSorting;
	const columnWidth = shouldResizeWithTable === true ? undefined : width;
	const isInteractive = isDraggable || isSortable;

	return (
		<HeaderCell
			scope="col"
			columnWidth={columnWidth}
			minColumnWidth={width}
			ref={headerRef} // aria-label is added to prevent the extra accessibility hints in the header cell from being
			// read as the column title when a user is navigating between columns in the table content.
			aria-label={column.title}
		>
			{isInteractive ? (
				<InteractiveHeaderCellContent
					column={column}
					headerRef={headerRef}
					index={index}
					instanceId={instanceId}
					isDraggable={isDraggable}
					isSortable={isSortable}
					reorderColumn={reorderColumn}
					totalColumns={totalColumns}
					totalColumnsExcludingMenu={totalColumnsExcludingMenu}
					visibleTableHeight={visibleTableHeight}
				/>
			) : (
				<ReadOnlyHeaderCellContent column={column} />
			)}
		</HeaderCell>
	);
};

export default TableHeaderCell;
